import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import Price from 'components/commercetools-ui/price';
import { useFormat } from 'helpers/hooks/useFormat';
import { useAccount, useCart } from 'frontastic';
import CreditCard from 'components/icons/credit-card';
import { FullPageWidthWrapper } from '../full-bleed';
import { Order } from '@Types/cart/Order';
import { useAnonymousStore } from 'helpers/stores/anonymousStore';

const ThankYou: FC = () => {
  //i18n messages
  const { formatMessage: formatCheckoutMessage } = useFormat({ name: 'checkout' });
  const router = useRouter();
  const { checkout, getThankYouOrder, getOrderShippingMethod } = useCart();
  const [order, setOrder] = useState<Partial<Order>>();
  const { loggedIn } = useAccount();
  const { anonymousId } = useAnonymousStore();
  const [loading, setLoading] = useState<boolean>(true);
  const [shippingDescription, setShippingDescription] = useState<string>('');

  // useEffect(() => {
  //   const { sessionId, redirectResult } = router.query;
  //   if (sessionId && redirectResult) {
  //     checkout();
  //   }
  // }, []);

  useEffect(() => {
    getThankYouOrder(router?.query?.orderId as string, anonymousId).then((res) => {
      if (res.orderId) {
        setOrder(res);
        setLoading(false);
        getOrderShippingMethod(res?.shippingInfo?.shippingMethod?.id).then((data) => {
          setShippingDescription(data.description);
        });
      } else {
        router.push('/login');
      }
    });
  }, [loggedIn]);

  // const discountPrice = order?.lineItems?.reduce((a, b) => {
  //   return (
  //     a +
  //     b.count *
  //     b.discounts.reduce((x, y) => {
  //       return x + y.discountedAmount.centAmount;
  //     }, 0)
  //   );
  // }, 0);

  const itemsTotalNumber = order?.lineItems?.reduce((accumulator, currentValue) => accumulator + currentValue.count, 0);

  const paymentMethod =
    order?.paymentInfo?.payments[order?.paymentInfo?.payments.length - 1].obj.paymentMethodInfo.method.toLowerCase() ===
    'paypal'
      ? 'Paypal'
      : order?.paymentInfo?.payments[
          order?.paymentInfo?.payments.length - 1
        ].obj.paymentMethodInfo.method.toLowerCase() === 'sofort'
      ? 'Sofort'
      : formatCheckoutMessage({
          id: 'payment.method.card',
          defaultMessage: 'Credit/Debit Card',
        });

  if (!order?.orderId) return <></>;

  const createdAt = new Date(order?.createdAt);

  return (
    <FullPageWidthWrapper>
      <section className="container mx-auto max-w-[1280px] bg-white px-4 sm:px-10 lg:px-4">
        <h2 className="'max-w-[526px] text-start my-8 text-xl font-medium text-[#242424]">
          {formatCheckoutMessage({
            id: 'orderPlaced',
            values: { orderNumber: order.orderNumber },
          })}
        </h2>
        <h2 className="pb-12 pt-4 text-xl font-normal text-[#242424]">
          {formatCheckoutMessage({
            id: 'orderEmailConfirmation',
            values: { email: order?.email },
          })}
        </h2>
        <div className="mb-4 flex items-center gap-2">
          <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g id="Group">
              <path
                id="Vector"
                d="M11.6484 8.42529H8.37344V4.72529C8.37344 4.42529 8.12344 4.17529 7.82344 4.17529C7.52344 4.17529 7.27344 4.42529 7.27344 4.72529V9.00029C7.27344 9.30029 7.52344 9.55029 7.82344 9.55029H11.6484C11.9484 9.55029 12.1984 9.30029 12.1984 9.00029C12.1984 8.70029 11.9734 8.42529 11.6484 8.42529Z"
                fill="#242424"
              />
              <path
                id="Vector_2"
                d="M7.99922 0.850098C3.82422 0.850098 0.449219 4.2751 0.449219 8.5001C0.449219 12.7251 3.82422 16.1501 7.99922 16.1501C12.1742 16.1501 15.5492 12.7251 15.5492 8.5001C15.5492 4.2751 12.1742 0.850098 7.99922 0.850098ZM7.99922 15.0251C4.44922 15.0251 1.54922 12.1001 1.54922 8.5001C1.54922 4.9001 4.44922 1.9751 7.99922 1.9751C11.5492 1.9751 14.4492 4.9001 14.4492 8.5001C14.4492 12.1001 11.5492 15.0251 7.99922 15.0251Z"
                fill="#242424"
              />
            </g>
          </svg>
          <p className=" text-base font-semibold text-[#242424]">
            {formatCheckoutMessage({
              id: 'timePlaced',
              defaultMessage: 'Time placed',
            })}
            : {createdAt.toLocaleDateString(router.locale, { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        <div className="flex w-full flex-row flex-wrap gap-6 lg:justify-between">
          <ThankYouCard title={formatCheckoutMessage({ id: 'shippingDetails', defaultMessage: 'Shipping Details' })}>
            <div className="flex flex-col gap-2">
              <p className="text-base font-semibold text-[#242424]">
                {order?.shippingAddress?.firstName} {order?.shippingAddress?.lastName}
              </p>
              <p className="text-base font-normal text-[#242424]">{order?.shippingAddress?.streetName}</p>
              <p className="text-base font-normal text-[#242424]">
                {order?.shippingAddress?.postalCode} {order?.shippingAddress?.city}
              </p>
              {order?.shippingAddress?.phone && (
                <p className="text-base font-normal text-[#242424]">{order?.shippingAddress?.phone}</p>
              )}
              <p className="text-base font-normal text-[#242424]">{order?.email}</p>
            </div>
          </ThankYouCard>
          <ThankYouCard title={formatCheckoutMessage({ id: 'billingDetails', defaultMessage: 'Billing Details' })}>
            <div className="flex flex-col gap-2">
              <p className="text-base font-normal text-[#242424]">
                {formatCheckoutMessage({ id: 'paymentMethod', defaultMessage: 'Payment Method' })}: {paymentMethod}
              </p>
              {paymentMethod === 'Paypal' ? (
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M22.7066 5.20635C22.2691 2.5376 20.1691 0.787598 17.3691 0.787598H8.48784C8.05034 0.787598 7.65659 1.09385 7.52534 1.53135L2.45034 22.2251C2.36284 22.5313 2.45034 22.8376 2.62534 23.0563C2.80034 23.2751 3.10659 23.4501 3.41284 23.4501H5.86284L5.25034 26.0313C5.16284 26.3376 5.25034 26.6438 5.42534 26.8626C5.60034 27.0813 5.90659 27.2563 6.21284 27.2563H12.5128C12.9503 27.2563 13.3878 26.9501 13.4753 26.5126L15.3566 18.3751H17.3253C19.1191 18.3751 20.9128 17.6751 22.4003 16.4063C23.8878 15.1813 24.9378 13.4751 25.3753 11.6813C26.0316 8.88135 24.9378 6.3001 22.7066 5.20635ZM9.27534 2.75635H17.4128C19.3378 2.75635 20.6941 4.0251 20.8253 5.90635C20.8691 6.51885 20.8253 7.0001 20.6941 7.4376C20.3878 8.7501 19.5566 10.1063 18.3753 11.0688C17.2378 12.0313 15.8816 12.5563 14.5691 12.5563H11.8566C11.4191 12.5563 11.0253 12.8626 10.8941 13.3001L8.92534 21.4376H4.68159L9.27534 2.75635ZM23.4941 11.2438C23.1878 12.6001 22.3566 13.9126 21.1753 14.8751C20.0378 15.8376 18.6816 16.3626 17.3691 16.3626H14.6128C14.1753 16.3626 13.7378 16.6688 13.6503 17.1063L11.7691 25.2438H7.43784L7.87534 23.4063H9.66909C10.1066 23.4063 10.5003 23.1001 10.6316 22.6626L12.6003 14.5251H14.5253C16.3191 14.5251 18.1128 13.8251 19.6441 12.5563C21.1316 11.2876 22.1816 9.6251 22.6191 7.8751C22.6628 7.74385 22.6628 7.65635 22.7066 7.5251C23.4941 8.44385 23.8441 9.75635 23.4941 11.2438Z"
                    fill="#242424"
                  />
                </svg>
              ) : (
                <CreditCard className="h-[28px] w-[28px]" />
              )}

              <div className="flex gap-2 text-base font-semibold text-[#242424]">
                <span>
                  {formatCheckoutMessage({
                    id: 'thankyou.total',
                    defaultMessage: 'Total Payable',
                  })}
                </span>
                <span>
                  <Price
                    price={{
                      centAmount: order?.sum?.centAmount,
                      currencyCode: order?.sum?.currencyCode,
                    }}
                    className="px-1 text-base font-semibold text-[#242424]"
                  />
                </span>
              </div>
              <p className="text-base font-normal text-[#242424]">
                {formatCheckoutMessage({ id: 'itemsOrdered', defaultMessage: 'Items ordered' })}: {itemsTotalNumber}
              </p>
            </div>
          </ThankYouCard>
          <ThankYouCard title={formatCheckoutMessage({ id: 'shippingMethod', defaultMessage: 'Shipping Method' })}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <svg width="65" height="20" viewBox="0 0 65 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_746_4756)">
                    <path d="M64.6828 0.00341797V14.2778H0V0.00341797H64.6828Z" fill="#FFCC00" />
                    <path
                      d="M11.3752 2.56689L9.30859 5.37451H20.5716C21.1409 5.37451 21.1334 5.59084 20.8551 5.9678C20.5725 6.35047 20.0996 7.01598 19.8117 7.4047C19.6657 7.60197 19.4016 7.96136 20.2768 7.96136H24.8827C24.8827 7.96136 25.6251 6.95117 26.2472 6.10654C27.0939 4.95773 26.3207 2.56701 23.2941 2.56701L11.3752 2.56689Z"
                      fill="#D2002E"
                    />
                    <path
                      d="M8.47238 11.7138L12.6232 6.07379H17.7741C18.3434 6.07379 18.336 6.29011 18.0577 6.66708C17.7751 7.04975 17.2945 7.70818 17.0066 8.0969C16.8604 8.29417 16.5964 8.65414 17.4715 8.65414H24.3722C23.7983 9.44036 21.9367 11.7137 18.5941 11.7137L8.47238 11.7138ZM32.2444 8.65345L29.9934 11.7136H24.0558C24.0558 11.7136 26.3055 8.65414 26.3074 8.65414L32.2444 8.65345ZM41.3406 7.96136H26.8175L30.789 2.56689H36.725L34.4486 5.66009H37.0982L39.3755 2.56689H45.3108L41.3406 7.96136ZM40.8309 8.65425L38.5789 11.7138H32.6434C32.6434 11.7138 34.8931 8.65425 34.895 8.65425H40.8309ZM0 9.86068H8.74735L8.26952 10.5107H0V9.86068ZM0 8.65425H9.63602L9.15729 9.30333H0V8.65425ZM0 11.0678H7.85913L7.38358 11.7138H0V11.0678ZM64.6831 10.5107H55.9694L56.4477 9.86079H64.6831V10.5107ZM64.6831 11.7138L55.0836 11.7147L55.5591 11.0678H64.6831V11.7138ZM57.3352 8.65425H64.6831V9.30367L56.8574 9.30424L57.3352 8.65425ZM53.5313 2.56689L49.5605 7.96125H43.2707C43.2707 7.96125 47.2425 2.56689 47.2445 2.56689H53.5313ZM42.7617 8.65425C42.7617 8.65425 42.3278 9.2472 42.1172 9.5322C41.3716 10.5399 42.0307 11.7138 44.4637 11.7138H53.9966L56.2484 8.65425H42.7617Z"
                      fill="#D2002E"
                    />
                    <path
                      d="M0 16.5986H64.6831V16.922H0V16.5986ZM0 18.1075H64.6831V18.4309H0V18.1075ZM0 19.6161H64.6831V19.9394H0V19.6161Z"
                      fill="#FFCC00"
                    />
                    <path
                      d="M21.7207 19.9391H23.6674L23.9781 19.5179H22.5294L23.3417 18.4172H24.661L24.9718 17.9959H23.6525L24.3729 17.0198H25.8217L26.1325 16.5985H24.1859L21.7207 19.9391ZM24.1097 19.9391H24.7071L26.7433 18.5225L26.7234 19.9391H27.3507L27.3553 18.1923L29.7013 16.5985H29.1338L27.2664 17.8812L27.2868 16.5985H26.6894L26.6712 18.2019L24.1097 19.9391ZM30.4549 17.0198H30.8781C31.1868 17.0198 31.5317 17.1729 31.221 17.594C30.896 18.0342 30.3982 18.1826 30.0895 18.1826H29.5966L30.4549 17.0198ZM27.8027 19.9391H28.3005L29.2858 18.6038H29.7737C30.401 18.6038 31.1976 18.3406 31.745 17.5987C32.3066 16.8377 31.8907 16.5983 31.1789 16.5983H30.2678L27.8027 19.9391ZM30.6695 19.9391H31.1674L32.2623 18.4554H32.5609C32.8247 18.4554 32.9892 18.4554 32.9034 18.7808L32.5814 19.9391H33.149L33.4737 18.6085C33.5161 18.4028 33.4945 18.331 33.3824 18.2734L33.3894 18.2638C33.8423 18.1967 34.3677 17.9097 34.668 17.5028C35.2577 16.7037 34.6733 16.5983 33.9861 16.5983H33.1349L30.6695 19.9391ZM33.3219 17.0198H33.7997C34.2579 17.0198 34.3555 17.2112 34.1331 17.5125C33.8965 17.8334 33.4693 18.0342 32.9963 18.0342H32.5731L33.3219 17.0198ZM33.6645 19.9391H35.6112L35.9217 19.5179H34.4732L35.2854 18.4172H36.6048L36.9154 17.9959H35.5962L36.3166 17.0198H37.7654L38.0763 16.5985H36.1297L33.6645 19.9391ZM40.4742 16.6607C40.2953 16.5794 40.0495 16.541 39.8206 16.541C39.1933 16.541 38.4723 16.8571 38.0343 17.4505C37.2679 18.4891 38.9134 18.2832 38.3623 19.0299C38.0728 19.4221 37.5614 19.5755 37.2777 19.5755C37.0237 19.5755 36.7957 19.4796 36.6759 19.4125L36.2978 19.8577C36.4738 19.9295 36.6635 19.9966 36.9074 19.9966C37.6043 19.9966 38.3853 19.7138 38.9115 19.0008C39.7237 17.9002 38.1206 18.0483 38.5939 17.4072C38.8519 17.0579 39.2511 16.9621 39.5298 16.9621C39.7888 16.9621 39.8962 17.0054 40.0794 17.101L40.4742 16.6607ZM42.9635 16.6607C42.7846 16.5794 42.539 16.541 42.3099 16.541C41.6826 16.541 40.9616 16.8571 40.5236 17.4505C39.7572 18.4891 41.4027 18.2832 40.8516 19.0299C40.5622 19.4221 40.0507 19.5755 39.767 19.5755C39.513 19.5755 39.2849 19.4796 39.1653 19.4125L38.7871 19.8577C38.9631 19.9295 39.1528 19.9966 39.3966 19.9966C40.0936 19.9966 40.8744 19.7138 41.4006 19.0008C42.213 17.9002 40.61 18.0483 41.0832 17.4072C41.3411 17.0579 41.7404 16.9621 42.0191 16.9621C42.2781 16.9621 42.3857 17.0054 42.5687 17.101L42.9635 16.6607Z"
                      fill="#D2002E"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_746_4756">
                      <rect width="64.6831" height="20" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <p className="text-base font-normal text-[#242424]">{order?.shippingInfo?.shippingMethodName}</p>
              </div>
              <p className="text-base font-normal text-[#242424]">{shippingDescription}</p>
            </div>
          </ThankYouCard>
        </div>
      </section>
    </FullPageWidthWrapper>
  );
};

export default ThankYou;

const ThankYouCard = ({ title, children }: { title: string; children: ReactNode }) => {
  return (
    <div className="w-full rounded bg-[#F6F9FF] p-8 md:w-5/12 lg:w-[370px]">
      <p className="mb-6 text-lg font-bold">{title}</p>
      {children}
    </div>
  );
};
